/**
 * Bankroll Simulation Panel (bankroll_simulation, admin-only while it is being tested)
 *
 * Monte Carlo bankroll projection UI.
 * User configures starting bankroll, days, strategy params → see projected outcomes.
 */

import { useBankrollSimulation } from '@hooks/useBankrollSimulation';
import { useFeatureAccess } from '@hooks/useFeatureAccess';
import { DollarSign } from 'lucide-react';
import { type ReactElement, useState } from 'react';

export function BankrollSimPanel(): ReactElement | null {
  const { isHidden } = useFeatureAccess();
  const { result, loading, simulate } = useBankrollSimulation();
  const [isOpen, setIsOpen] = useState(false);

  // Config
  const [bankroll, setBankroll] = useState(1000);
  const [days, setDays] = useState(90);

  const hidden = isHidden('bankroll_simulation');

  if (hidden) return null;

  return (
    <div className="border border-theme-border rounded-lg overflow-hidden">
      <button
        type="button"
        onClick={() => setIsOpen((v) => !v)}
        className="w-full flex items-center justify-between p-3 bg-theme-surface hover:bg-theme-surface-hover transition text-left"
      >
        <div className="flex items-center gap-2">
          <DollarSign className="w-4 h-4 text-yellow-400" />
          <span className="text-sm font-medium text-white">Bankroll Simulation</span>
        </div>
        <span className="text-xs text-theme-text-tertiary">{isOpen ? 'Hide' : 'Show'}</span>
      </button>

      {isOpen && (
        <div className="p-3 border-t border-theme-border space-y-3">
          {/* Config */}
          <div className="flex gap-3 items-end">
            <div className="flex-1">
              <label className="text-[10px] text-theme-text-tertiary block mb-1">
                Bankroll ($)
              </label>
              <input
                type="number"
                value={bankroll}
                onChange={(e) => setBankroll(Number(e.target.value))}
                className="w-full bg-theme-surface-hover text-white text-sm rounded px-2 py-1.5 border border-theme-border"
              />
            </div>
            <div className="flex-1">
              <label className="text-[10px] text-theme-text-tertiary block mb-1">Days</label>
              <select
                value={days}
                onChange={(e) => setDays(Number(e.target.value))}
                className="w-full bg-theme-surface-hover text-white text-sm rounded px-2 py-1.5 border border-theme-border"
              >
                <option value={30}>30 days</option>
                <option value={60}>60 days</option>
                <option value={90}>90 days</option>
                <option value={180}>180 days</option>
                <option value={365}>1 year</option>
              </select>
            </div>
            <button
              type="button"
              onClick={() => simulate(bankroll, days)}
              disabled={loading}
              className="px-4 py-1.5 bg-yellow-600/20 text-yellow-400 text-sm font-medium rounded hover:bg-yellow-600/30 transition disabled:opacity-50"
            >
              {loading ? 'Running...' : 'Simulate'}
            </button>
          </div>

          {/* Results */}
          {result && (
            <div className="space-y-2">
              <div className="grid grid-cols-3 gap-2 text-xs">
                <div className="bg-theme-surface rounded p-2 text-center">
                  <div className="text-theme-text-tertiary">Profit Prob</div>
                  <div
                    className={`text-lg font-bold ${result.profitProbability > 0.5 ? 'text-green-400' : 'text-red-400'}`}
                  >
                    {(result.profitProbability * 100).toFixed(0)}%
                  </div>
                </div>
                <div className="bg-theme-surface rounded p-2 text-center">
                  <div className="text-theme-text-tertiary">Expected</div>
                  <div className="text-lg font-bold text-white">
                    ${result.expectedFinal.toFixed(0)}
                  </div>
                </div>
                <div className="bg-theme-surface rounded p-2 text-center">
                  <div className="text-theme-text-tertiary">ROI</div>
                  <div
                    className={`text-lg font-bold ${result.expectedRoi > 0 ? 'text-green-400' : 'text-red-400'}`}
                  >
                    {result.expectedRoi > 0 ? '+' : ''}
                    {result.expectedRoi.toFixed(1)}%
                  </div>
                </div>
              </div>

              {/* Percentile range */}
              <div className="text-[10px] text-theme-text-tertiary">
                <div className="flex justify-between">
                  <span>Worst 5%: ${result.percentiles.p5.toFixed(0)}</span>
                  <span>Median: ${result.percentiles.p50.toFixed(0)}</span>
                  <span>Best 5%: ${result.percentiles.p95.toFixed(0)}</span>
                </div>
                <div className="mt-1 h-2 bg-theme-surface rounded-full overflow-hidden flex">
                  <div className="bg-red-500/50 h-full" style={{ width: '20%' }} />
                  <div className="bg-yellow-500/50 h-full" style={{ width: '30%' }} />
                  <div className="bg-green-500/50 h-full" style={{ width: '30%' }} />
                  <div className="bg-green-400/50 h-full" style={{ width: '20%' }} />
                </div>
                <div className="mt-1 flex justify-between">
                  <span>Ruin: {(result.ruinProbability * 100).toFixed(1)}%</span>
                  <span>Max Drawdown: {result.avgMaxDrawdown.toFixed(0)}%</span>
                </div>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
